import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  FileSearch,
  TrendingDown,
  Mail,
  Sparkles,
  ArrowUpRight,
  Building2,
  ScanLine,
  CheckCircle2,
  Clock,
} from "lucide-react";

const BUREAU_COLORS = {
  equifax: "text-[#7CFF9B]",
  experian: "text-[#D5FF3F]",
  transunion: "text-[#B8B0FF]",
  all: "text-[#F5F3EE]",
};

const ITEM_TYPE_LABEL = {
  collection: "Collection",
  charge_off: "Charge-off",
  late_payment: "Late payment",
  inquiry: "Hard inquiry",
  bankruptcy: "Bankruptcy",
  repossession: "Repossession",
  foreclosure: "Foreclosure",
  judgment: "Judgment",
  lien: "Lien",
};

function statusBadge(counts = {}) {
  if (counts.resolved) return { text: `${counts.resolved} resolved`, cls: "bg-[#7CFF9B]/15 text-[#7CFF9B]" };
  if (counts.sent) return { text: `${counts.sent} letter${counts.sent === 1 ? "" : "s"} mailed`, cls: "bg-[#D5FF3F]/15 text-[#D5FF3F]" };
  if (counts.draft) return { text: `${counts.draft} draft${counts.draft === 1 ? "" : "s"}`, cls: "bg-[#F5F3EE]/10 text-[#F5F3EE]" };
  return { text: "No action yet", cls: "bg-[#6D716C]/10 text-[#6D716C]" };
}

export default function ReportReview() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: latest }, { data: hist }] = await Promise.all([
          api.get("/scan/latest"),
          api.get("/scans/history"),
        ]);
        setData(latest);
        setHistory(hist.scans || []);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const scan = data?.scan;
  const items = data?.items || [];
  const totals = data?.totals || {};
  const hasScan = !!scan;

  return (
    <AppShell>
      <div className="mb-8">
        <div className="overline mb-3">Credit report</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">
          Every detail
          <span className="text-[#D5FF3F]"> reviewed.</span>
        </h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl">
          Full breakdown of your latest scan — what Aura flagged, what&rsquo;s already in dispute, and
          what needs your attention next.
        </p>
      </div>

      {loading ? (
        <div className="text-[#A8ADA5] font-mono">Loading review…</div>
      ) : !hasScan ? (
        <EmptyState />
      ) : (
        <>
          {/* Summary bento */}
          <div className="grid md:grid-cols-4 gap-px bg-[#26282C] border border-[#26282C] mb-8">
            <SummaryCard
              overline="Est. score"
              value={scan.estimated_score || "—"}
              hint="Aura estimate"
              color="text-[#F5F3EE]"
              testid="report-est-score"
            />
            <SummaryCard
              overline="Items flagged"
              value={totals.items || 0}
              hint="derogatory / inaccurate"
              color="text-[#FF6B4A]"
              testid="report-items-count"
            />
            <SummaryCard
              overline="Letters mailed"
              value={totals.disputes_sent || 0}
              hint="via USPS certified"
              color="text-[#D5FF3F]"
              testid="report-letters-sent"
            />
            <SummaryCard
              overline="Resolved"
              value={totals.disputes_resolved || 0}
              hint="bureaus deleted"
              color="text-[#7CFF9B]"
              testid="report-resolved"
            />
          </div>

          {/* Latest scan meta */}
          <div className="card-surface p-5 mb-8 flex items-start gap-4 flex-wrap" data-testid="report-scan-meta">
            <div className="w-10 h-10 rounded-sm bg-[#D5FF3F]/10 border border-[#D5FF3F]/25 flex items-center justify-center flex-shrink-0">
              <ScanLine size={18} className="text-[#D5FF3F]" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="overline mb-1">Latest scan</div>
              <div className="text-[#F5F3EE]">
                <span className={`font-medium ${BUREAU_COLORS[scan.detected_bureau] || "text-[#F5F3EE]"}`}>
                  {(scan.detected_bureau || "unknown").toUpperCase()}
                </span>
                {" · "}
                <span className="text-[#A8ADA5]">
                  {new Date(scan.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                </span>
              </div>
              {scan.summary && (
                <p className="text-sm text-[#A8ADA5] mt-2 leading-relaxed max-w-2xl">
                  {scan.summary}
                </p>
              )}
            </div>
            <Link to="/app/get-report">
              <Button className="rounded-full btn-outline h-10 px-4" data-testid="report-rescan-btn">
                Re-scan report <ArrowUpRight size={13} className="ml-1.5" />
              </Button>
            </Link>
          </div>

          {/* Items list */}
          <div className="mb-4 flex items-center justify-between">
            <div className="overline">Flagged items ({items.length})</div>
            {items.length > 0 && (
              <Link to="/app/items" className="text-xs text-[#D5FF3F] hover:underline font-mono uppercase tracking-widest">
                Manage in Items →
              </Link>
            )}
          </div>

          {items.length === 0 ? (
            <div className="card-surface p-8 text-center text-[#A8ADA5]">
              Nothing flagged — your report came back clean.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="report-items-list">
              {items.map((it) => {
                const badge = statusBadge(it.dispute_counts);
                const bureauCls = BUREAU_COLORS[it.bureau] || "text-[#F5F3EE]";
                return (
                  <li
                    key={it.id}
                    className="card-surface p-4 md:p-5 grid grid-cols-12 gap-3 items-center"
                    data-testid={`report-item-${it.id}`}
                  >
                    <div className="col-span-12 md:col-span-5 flex items-start gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-sm bg-[#FF6B4A]/10 border border-[#FF6B4A]/25 flex items-center justify-center flex-shrink-0">
                        <TrendingDown size={15} className="text-[#FF6B4A]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[#F5F3EE] font-medium truncate">
                          {it.creditor || "Unknown creditor"}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C] mt-0.5">
                          {ITEM_TYPE_LABEL[it.item_type] || it.item_type || "item"}
                          {it.account_number ? ` · ${it.account_number}` : ""}
                        </div>
                      </div>
                    </div>

                    <div className="col-span-4 md:col-span-2 text-xs">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">Bureau</div>
                      <div className={`font-medium ${bureauCls}`}>
                        {(it.bureau || "?").toUpperCase()}
                      </div>
                    </div>

                    <div className="col-span-4 md:col-span-2 text-xs">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">Amount</div>
                      <div className="font-mono text-[#F5F3EE]">
                        {it.amount ? `$${Number(it.amount).toLocaleString()}` : "—"}
                      </div>
                    </div>

                    <div className="col-span-4 md:col-span-2 text-xs">
                      <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${badge.cls}`}>
                        {badge.text}
                      </span>
                    </div>

                    <div className="col-span-12 md:col-span-1 flex justify-end">
                      <Link to="/app/items">
                        <Button size="sm" className="rounded-full btn-outline h-8 px-3 text-xs" data-testid={`report-item-open-${it.id}`}>
                          Open
                        </Button>
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Scan history timeline */}
          {history.length > 1 && (
            <>
              <div className="overline mb-3 mt-10">Scan history</div>
              <ul className="space-y-2" data-testid="report-scan-history">
                {history.map((s) => (
                  <li key={s.id} className="card-surface p-3 flex items-center gap-3 text-sm">
                    <Clock size={14} className="text-[#D5FF3F] flex-shrink-0" />
                    <span className="text-[#F5F3EE]">
                      {new Date(s.created_at).toLocaleDateString()}
                    </span>
                    <span className="text-[#6D716C]">·</span>
                    <span className="text-[#A8ADA5]">
                      {(s.detected_bureau || "unknown").toUpperCase()} · {s.item_count || 0} items ·
                      est. score {s.estimated_score || "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Next-step callouts */}
          <div className="grid md:grid-cols-2 gap-4 mt-10">
            <CallOut
              icon={Sparkles}
              title="Ready to escalate?"
              body="Draft dispute letters for every flagged item — Aura writes one per bureau in seconds."
              cta="Draft letters"
              to="/app/items"
              testid="report-cta-drafts"
            />
            <CallOut
              icon={Mail}
              title="Send everything USPS"
              body="Mail your drafts via USPS Certified with tracking. The FCRA 30-day clock only starts once the bureaus receive them."
              cta="Mail all"
              to="/app/disputes"
              testid="report-cta-mail"
            />
          </div>
        </>
      )}
    </AppShell>
  );
}

function SummaryCard({ overline, value, hint, color, testid }) {
  return (
    <div className="bg-[#0B0C0E] p-6" data-testid={testid}>
      <div className="overline mb-2">{overline}</div>
      <div className={`ticker text-4xl ${color}`}>{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C] mt-1">
        {hint}
      </div>
    </div>
  );
}

function CallOut({ icon: Icon, title, body, cta, to, testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-sm bg-[#D5FF3F]/10 border border-[#D5FF3F]/25 flex items-center justify-center flex-shrink-0">
          <Icon size={16} className="text-[#D5FF3F]" />
        </div>
        <div>
          <div className="text-[#F5F3EE] font-medium">{title}</div>
          <p className="text-sm text-[#A8ADA5] mt-1 leading-relaxed">{body}</p>
        </div>
      </div>
      <Link to={to}>
        <Button className="rounded-full btn-outline h-10 px-4 hover:!bg-[#D5FF3F] hover:!text-[#0B0C0E] hover:!border-[#D5FF3F]">
          {cta} <ArrowUpRight size={13} className="ml-1.5" />
        </Button>
      </Link>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card-surface p-10 text-center" data-testid="report-empty">
      <div className="w-14 h-14 rounded-full bg-[#D5FF3F]/10 border border-[#D5FF3F]/25 flex items-center justify-center mx-auto mb-4">
        <FileSearch size={22} className="text-[#D5FF3F]" />
      </div>
      <h2 className="font-display text-2xl mb-2">No scan yet</h2>
      <p className="text-[#A8ADA5] max-w-md mx-auto mb-6">
        Once you scan a credit report, this page shows the full breakdown — every flagged item,
        every dispute in flight, and what to do next.
      </p>
      <Link to="/app/get-report">
        <Button className="rounded-full btn-lime h-11 px-6">
          Get your report <ArrowUpRight size={14} className="ml-1.5" />
        </Button>
      </Link>
    </div>
  );
}
